import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  ContactSupport as ContactSupportIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  Group as GroupIcon,
  HelpOutline as HelpOutlineIcon,
  ListAlt as ListAltIcon,
  MailOutline as MailOutlineIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Search as SearchIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../layout/MainLayout';

const gettingStartedSteps = [
  {
    title: '1. Prepare data catalog',
    description: 'Create List A (cohort parameters) and List B (extraction) to start your workflow cleanly.',
    actionLabel: 'Go to Data Catalog',
    actionPath: '/catalog'
  },
  {
    title: '2. Build cohort',
    description: 'Define and refine your population in Cohort Builder (list and graph view).',
    actionLabel: 'Go to Cohort Builder',
    actionPath: '/cohorts'
  },
  {
    title: '3. Submit request',
    description: 'Combine WHO (cohort) and WHAT (List B) and create a data request.',
    actionLabel: 'Go to Requests',
    actionPath: '/requests'
  }
];

const workflowGuides = [
  {
    title: 'Data Catalog',
    icon: <FolderIcon color="primary" />,
    topics: [
      'Create and manage parameter lists',
      'List A = cohort parameters used for inclusion/exclusion criteria in Cohort Builder',
      'List B = extraction fields for requests'
    ],
    path: '/catalog'
  },
  {
    title: 'Cohort Builder',
    icon: <GroupIcon color="primary" />,
    topics: [
      'Switch between list and graph view',
      'Create sub-cohorts for refinements',
      'Edit criteria and simulate execution'
    ],
    path: '/cohorts'
  },
  {
    title: 'Requests',
    icon: <ListAltIcon color="primary" />,
    topics: [
      'Create request with cohort + List B',
      'Track status and progress',
      'Review and export results'
    ],
    path: '/requests'
  }
];

const faqItems = [
  {
    question: 'What is the difference between List A and List B?',
    answer: 'List A defines the cohort parameters used in Cohort Builder to build cohort criteria (WHO), while List B defines the data fields to extract (WHAT).'
  },
  {
    question: 'What is a sub-cohort and when should I use it?',
    answer: 'A sub-cohort is a refinement of an existing cohort with additional criteria.'
  },
  {
    question: 'How do I switch between list view and graph view?',
    answer: 'In Cohort Builder, use the top toggle to switch between list and graph view.'
  },
  {
    question: 'How do I refine a cohort with additional criteria?',
    answer: 'Open the cohort, edit include/exclude criteria, and save your changes.'
  },
  {
    question: 'How long does a data request take?',
    answer: 'It depends on data scope and cohort complexity. Request status is shown transparently.'
  },
  {
    question: 'Can I edit a cohort later?',
    answer: 'Yes. Cohorts can be updated and executed again. Changes affect downstream steps.'
  },
  {
    question: 'What do node statuses in the graph mean?',
    answer: 'Statuses indicate editing and execution state (e.g., unedited, executing, completed, error).'
  }
];

const glossaryItems = [
  { term: 'Cohort', definition: 'Defined patient group with specific criteria.' },
  { term: 'Sub-Cohort', definition: 'Refined subset of a parent cohort.' },
  { term: 'Cohort Builder', definition: 'Tool to create and maintain hierarchical cohorts.' },
  { term: 'Graph View', definition: 'Interactive node view of the cohort structure.' },
  { term: 'List View', definition: 'Table view to manage cohorts.' },
  { term: 'Parameter List', definition: 'Collection of clinical parameters used either for cohort definition or extraction.' },
  { term: 'List A', definition: 'Parameter list of cohort parameters used in Cohort Builder to define inclusion/exclusion logic.' },
  { term: 'List B', definition: 'Parameter list for data extraction in requests.' },
  { term: 'Inclusion Criteria', definition: 'Conditions patients must satisfy.' },
  { term: 'Exclusion Criteria', definition: 'Conditions that exclude patients.' },
  { term: 'Breakpoint', definition: 'Marker to pause execution at the selected node.' },
  { term: 'Request', definition: 'Request built from cohort (WHO) + extraction list (WHAT).' }
];

const contactItems = [
  {
    title: 'Support Email',
    description: 'Get support from our team for questions and problems.',
    value: 'support@argus-health.de',
    href: 'mailto:support@argus-health.de',
    icon: <MailOutlineIcon color="primary" />
  },
  {
    title: 'Bug Report',
    description: 'Capture technical issues directly as a ticket.',
    value: 'Create issue in GitLab',
    href: 'https://gitlab.it.argus-evaluation.com/argus/applications/argus-platform/-/issues/new',
    icon: <BugReportIcon color="primary" />
  },
  {
    title: 'Feature Request',
    description: 'Document workflow improvement ideas.',
    value: 'Suggest new feature',
    href: 'https://gitlab.it.argus-evaluation.com/argus/applications/argus-platform/-/issues/new',
    icon: <SendIcon color="primary" />
  }
];

const matchesSearch = (value, searchTerm) =>
  value.toLowerCase().includes(searchTerm.toLowerCase());

const HelpSupportPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const translatedGettingStartedSteps = useMemo(
    () => t('supportPage.content.gettingStartedSteps', { returnObjects: true, defaultValue: gettingStartedSteps }),
    [t]
  );
  const translatedWorkflowGuides = useMemo(
    () => t('supportPage.content.workflowGuides', { returnObjects: true, defaultValue: workflowGuides }),
    [t]
  );
  const translatedFaqItems = useMemo(
    () => t('supportPage.content.faqItems', { returnObjects: true, defaultValue: faqItems }),
    [t]
  );
  const translatedGlossaryItems = useMemo(
    () => t('supportPage.content.glossaryItems', { returnObjects: true, defaultValue: glossaryItems }),
    [t]
  );
  const translatedContactItems = useMemo(
    () => t('supportPage.content.contactItems', { returnObjects: true, defaultValue: contactItems }),
    [t]
  );

  const filteredGettingStarted = useMemo(() => {
    if (!searchTerm.trim()) return translatedGettingStartedSteps;
    return translatedGettingStartedSteps.filter(
      (step) => matchesSearch(step.title, searchTerm) || matchesSearch(step.description, searchTerm)
    );
  }, [searchTerm, translatedGettingStartedSteps]);

  const filteredWorkflowGuides = useMemo(() => {
    if (!searchTerm.trim()) return translatedWorkflowGuides;
    return translatedWorkflowGuides.filter(
      (guide) =>
        matchesSearch(guide.title, searchTerm) ||
        guide.topics.some((topic) => matchesSearch(topic, searchTerm))
    );
  }, [searchTerm, translatedWorkflowGuides]);

  const filteredFaqItems = useMemo(() => {
    if (!searchTerm.trim()) return translatedFaqItems;
    return translatedFaqItems.filter(
      (item) => matchesSearch(item.question, searchTerm) || matchesSearch(item.answer, searchTerm)
    );
  }, [searchTerm, translatedFaqItems]);

  const filteredGlossaryItems = useMemo(() => {
    if (!searchTerm.trim()) return translatedGlossaryItems;
    return translatedGlossaryItems.filter(
      (item) => matchesSearch(item.term, searchTerm) || matchesSearch(item.definition, searchTerm)
    );
  }, [searchTerm, translatedGlossaryItems]);

  const filteredContactItems = useMemo(() => {
    if (!searchTerm.trim()) return translatedContactItems;
    return translatedContactItems.filter(
      (item) =>
        matchesSearch(item.title, searchTerm) ||
        matchesSearch(item.description, searchTerm) ||
        matchesSearch(item.value, searchTerm)
    );
  }, [searchTerm, translatedContactItems]);

  const showNoResults = (itemsLength) =>
    searchTerm.trim() && itemsLength === 0 ? (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('supportPage.noResults', { term: searchTerm })}
      </Alert>
    ) : null;

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('supportPage.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('supportPage.subtitle')}
        </Typography>

        <Paper sx={{ p: 2.5, mb: 3 }}>
          <TextField
            fullWidth
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('supportPage.searchPlaceholder')}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={t('supportPage.chips.gettingStarted')} size="small" />
            <Chip label={t('supportPage.chips.workflow')} size="small" />
            <Chip label={t('supportPage.chips.faqs')} size="small" />
            <Chip label={t('supportPage.chips.glossary')} size="small" />
            <Chip label={t('supportPage.chips.contact')} size="small" />
          </Stack>
        </Paper>

        <Paper>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label={t('supportPage.tabAriaLabel')}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<PlayCircleOutlineIcon />} iconPosition="start" label={t('supportPage.tabs.gettingStarted')} sx={{ textTransform: 'none' }} />
            <Tab icon={<HelpOutlineIcon />} iconPosition="start" label={t('supportPage.tabs.workflow')} sx={{ textTransform: 'none' }} />
            <Tab icon={<ContactSupportIcon />} iconPosition="start" label={t('supportPage.tabs.faqs')} sx={{ textTransform: 'none' }} />
            <Tab icon={<ListAltIcon />} iconPosition="start" label={t('supportPage.tabs.glossary')} sx={{ textTransform: 'none' }} />
            <Tab icon={<MailOutlineIcon />} iconPosition="start" label={t('supportPage.tabs.contact')} sx={{ textTransform: 'none' }} />
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('supportPage.sections.gettingStartedTitle')}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t('supportPage.sections.gettingStartedSubtitle')}
                </Typography>
                <Grid container spacing={2}>
                  {filteredGettingStarted.map((step) => (
                    <Grid item xs={12} md={4} key={step.title}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            {step.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {step.description}
                          </Typography>
                          <Button component={RouterLink} to={step.actionPath} variant="outlined" size="small">
                            {step.actionLabel}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {showNoResults(filteredGettingStarted.length)}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('supportPage.sections.workflowTitle')}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t('supportPage.sections.workflowSubtitle')}
                </Typography>

                <Grid container spacing={2}>
                  {filteredWorkflowGuides.map((guide) => (
                    <Grid item xs={12} md={4} key={guide.title}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {guide.icon}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {guide.title}
                            </Typography>
                          </Box>
                          <List dense sx={{ py: 0 }}>
                            {guide.topics.map((topic) => (
                              <ListItem key={topic} sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                </ListItemIcon>
                                <ListItemText primary={topic} />
                              </ListItem>
                            ))}
                          </List>
                          <Divider sx={{ my: 1.5 }} />
                          <Button component={RouterLink} to={guide.path} variant="text" size="small">
                            {t('supportPage.actions.openSection')}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {showNoResults(filteredWorkflowGuides.length)}
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('supportPage.sections.faqTitle')}
                </Typography>
                {filteredFaqItems.map((item) => (
                  <Accordion key={item.question} disableGutters variant="outlined" sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary">{item.answer}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
                {showNoResults(filteredFaqItems.length)}
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('supportPage.sections.glossaryTitle')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: '30%' }}>{t('supportPage.sections.glossaryTerm')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('supportPage.sections.glossaryDefinition')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredGlossaryItems.map((item) => (
                        <TableRow key={item.term} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{item.term}</TableCell>
                          <TableCell>{item.definition}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {showNoResults(filteredGlossaryItems.length)}
              </Box>
            )}

            {activeTab === 4 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('supportPage.sections.contactTitle')}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t('supportPage.sections.contactSubtitle')}
                </Typography>
                <Grid container spacing={2}>
                  {filteredContactItems.map((item) => (
                    <Grid item xs={12} md={4} key={item.title}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {item.icon}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {item.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {item.description}
                          </Typography>
                          <Button
                            href={item.href}
                            target={item.href.startsWith('http') ? '_blank' : undefined}
                            rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                            variant="outlined"
                            fullWidth
                          >
                            {item.value}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {showNoResults(filteredContactItems.length)}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default HelpSupportPage;
